import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Newspaper, ExternalLink, Clock, Crown, RefreshCw } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import SEO from '../../components/SEO';

const ExploreNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  const fetchNews = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const response = await apiFetch(`/api/news/articles?limit=100`);
      if (response.ok) {
        const data = await response.json();
        if (data.articles && data.articles.length > 0) {
          setNews(data.articles);
          setLastUpdated(new Date());
        } else {
          setError('No news articles found');
        }
      } else {
        setError('Failed to fetch news');
      }
    } catch (err) {
      setError('Unable to connect to news service');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleRefresh = () => {
    fetchNews(true);
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO title="Michael Jackson News — Latest Updates & Stories | PopCrown" description="Latest Michael Jackson news, biopic updates, fan community stories, and MJ legacy news. Stay informed with PopCrown." keywords="Michael Jackson news, MJ news, Michael Jackson biopic news, King of Pop updates, Michael Jackson latest, MJ community news, Michael Jackson 2026" canonical="/explore/news" />
      {/* Header */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            Back to Explore
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Newspaper className="w-12 h-12 text-[#E10600]" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  Latest <span className="thriller-text">News</span>
                </h1>
                <p className="text-gray-400">Stay updated with the latest news</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {lastUpdated && (
                <span className="text-gray-500 text-sm hidden sm:block">
                  Updated: {lastUpdated.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105 disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                  color: '#000'
                }}
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="font-semibold">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-[#E10600] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading latest MJ news from Google News...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                  color: '#000'
                }}
              >
                Try Again
              </button>
            </div>
          ) : news.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No news articles available</p>
              <button
                onClick={handleRefresh}
                className="px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                  color: '#000'
                }}
              >
                Refresh News
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {news.map((item, index) => (
                  <a
                    key={`${item.title}-${index}`}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      // Ensure link opens properly on mobile
                      e.preventDefault();
                      window.open(item.url, '_blank', 'noopener,noreferrer');
                    }}
                    className="group p-6 rounded-xl transition-all duration-300 hover:transform hover:scale-105 cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                      border: '2px solid rgba(225, 6, 0, 0.2)'
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs text-[#E10600] font-semibold">{item.source}</span>
                      <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-[#E10600] transition-colors" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3 group-hover:text-[#E10600] transition-colors line-clamp-3">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>{item.date || 'Recent'}</span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="text-center mt-8">
                <p className="text-gray-500 text-sm">
                  News sourced live from Google News • Click any article to read on the source website
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-10 h-10 mx-auto mb-4 object-contain" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Want More? Join the <span className="thriller-text">Community</span>
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Get exclusive access to community discussions, interactive features, and connect with fans worldwide.
          </p>
          <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
            Become a Member
          </a>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs">
            News sourced from public outlets. PopCrown is not affiliated with any artist estates or rights holders.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExploreNews;
